import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Filter, ShoppingCart, Building, Star, Download, Eye, Calendar } from "lucide-react";
import Header from "../components/Header";

const ThemesPage = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTheme, setSelectedTheme] = useState(null);

  const themes = [
    {
      id: 1,
      name: "Nexus Commerce",
      category: "ecommerce",
      type: "Premium",
      price: "$89",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      description: "Modern e-commerce template with advanced product filtering and cart functionality",
      features: ["Responsive Design", "Product Variants", "Wishlist", "Multi-currency"],
      rating: 4.9,
      sales: 1247,
      lastUpdated: "2024-05-15"
    },
    {
      id: 2,
      name: "Corporate Pro",
      category: "business",
      type: "Premium",
      price: "$79",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      description: "Professional business template perfect for corporate websites and agencies",
      features: ["Landing Pages", "Blog Layout", "Team Sections", "Contact Forms"],
      rating: 4.8,
      sales: 892,
      lastUpdated: "2024-05-10"
    },
    {
      id: 3,
      name: "ShopFlex",
      category: "ecommerce",
      type: "Free",
      price: "Free",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      description: "Flexible e-commerce solution for small to medium businesses",
      features: ["Mobile First", "SEO Optimized", "Fast Loading", "Easy Customization"],
      rating: 4.6,
      sales: 2156,
      lastUpdated: "2024-05-08"
    },
    {
      id: 4,
      name: "Startup Suite",
      category: "business",
      type: "Premium",
      price: "$65",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      description: "Complete template package for startups and growing businesses",
      features: ["Pricing Tables", "Testimonials", "Portfolio", "Newsletter"],
      rating: 4.7,
      sales: 734,
      lastUpdated: "2024-05-12"
    },
    {
      id: 5,
      name: "MarketMaster",
      category: "ecommerce",
      type: "Premium",
      price: "$99",
      image: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      description: "Advanced e-commerce platform with multi-vendor support",
      features: ["Multi-vendor", "Advanced Analytics", "Subscription", "Marketplace"],
      rating: 5.0,
      sales: 567,
      lastUpdated: "2024-05-18"
    },
    {
      id: 6,
      name: "Enterprise Plus",
      category: "business",
      type: "Premium",
      price: "$120",
      image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80",
      description: "Comprehensive business solution for large enterprises",
      features: ["Custom Dashboard", "CRM Integration", "Multi-language", "Advanced Security"],
      rating: 4.9,
      sales: 423,
      lastUpdated: "2024-05-05"
    },
    {
      id: 7,
      name: "Boutique Shop",
      category: "ecommerce",
      type: "Free",
      price: "Free",
      image: "https://images.unsplash.com/photo-1607082349566-187342175e2f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      description: "Elegant template for fashion and boutique stores",
      features: ["Lookbook", "Size Guide", "Product Zoom", "Social Sharing"],
      rating: 4.5,
      sales: 1789,
      lastUpdated: "2024-05-14"
    },
    {
      id: 8,
      name: "Consulting Pro",
      category: "business",
      type: "Premium",
      price: "$85",
      image: "https://images.unsplash.com/photo-1552664688-cf412ec27db2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2047&q=80",
      description: "Professional template for consulting firms and service providers",
      features: ["Service Pages", "Case Studies", "Team Profiles", "Appointment Booking"],
      rating: 4.8,
      sales: 658,
      lastUpdated: "2024-05-09"
    }
  ];

  const filters = [
    { id: "all", label: "All Themes", icon: <Filter className="w-4 h-4" /> },
    { id: "ecommerce", label: "Ecommerce", icon: <ShoppingCart className="w-4 h-4" /> },
    { id: "business", label: "Business", icon: <Building className="w-4 h-4" /> }
  ];

  const filteredThemes = useMemo(() => {
    return themes.filter(theme => {
      const matchesFilter = activeFilter === "all" || theme.category === activeFilter;
      const matchesSearch = theme.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           theme.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [activeFilter, searchQuery]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Header />
      {/* Header Section */}
      <section className="relative py-20 px-6 md:px-16 lg:px-24 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center mb-4">
            <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
            <p className="text-blue-600 font-semibold text-lg tracking-wider">
              PREMIUM TEMPLATES
            </p>
            <div className="w-2 h-2 bg-blue-600 rounded-full ml-3"></div>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
            Professional Website
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
              Themes & Templates
            </span>
          </h1>
          
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 mx-auto rounded-full mb-8"></div>
          
          <p className="text-gray-600 text-lg md:text-xl max-w-2xl mx-auto mb-12">
            Discover our collection of professionally designed themes and templates 
            to kickstart your next project with style and functionality.
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search themes by name or feature..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-6 py-4 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="px-6 md:px-16 lg:px-24 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter) => (
              <motion.button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeFilter === filter.id
                    ? "bg-blue-600 text-white shadow-lg"
                    : "bg-white/80 text-gray-700 hover:bg-blue-50 hover:text-blue-600 backdrop-blur-sm border border-white/50"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {filter.icon}
                <span>{filter.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Themes Grid */}
      <section className="px-6 md:px-16 lg:px-24 pb-20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <AnimatePresence>
              {filteredThemes.map((theme) => (
                <motion.div
                  key={theme.id}
                  variants={itemVariants}
                  layout
                  className="group relative"
                  whileHover={{ y: -5 }}
                >
                  {/* Theme Card */}
                  <div className="bg-white/80 backdrop-blur-sm rounded-3xl overflow-hidden shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-500 h-full flex flex-col">
                    
                    {/* Image Container */}
                    <div className="relative overflow-hidden">
                      <motion.img
                        src={theme.image}
                        alt={theme.name}
                        className="w-full h-48 object-cover transform group-hover:scale-110 transition-transform duration-700"
                        whileHover={{ scale: 1.05 }}
                      />
                      
                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold shadow-lg ${
                          theme.category === "ecommerce" 
                            ? "bg-green-500 text-white" 
                            : "bg-blue-500 text-white"
                        }`}>
                          {theme.category === "ecommerce" ? "Ecommerce" : "Business"}
                        </span>
                      </div>

                      {/* Type Badge */}
                      <div className="absolute top-4 right-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold shadow-lg ${
                          theme.type === "Premium" 
                            ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white" 
                            : "bg-gray-600 text-white"
                        }`}>
                          {theme.type}
                        </span>
                      </div>

                      {/* Overlay Gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      {/* Action Buttons */}
                      <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500 flex space-x-2">
                        <motion.button
                          className="bg-white text-gray-900 px-4 py-2 rounded-full font-semibold shadow-lg hover:bg-gray-50 transition-colors duration-200 flex items-center space-x-2"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Eye className="w-4 h-4" />
                          <span>Preview</span>
                        </motion.button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                          {theme.name}
                        </h3>
                        <div className="text-lg font-bold text-blue-600">
                          {theme.price}
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-gray-600 leading-relaxed mb-4 text-sm flex-1">
                        {theme.description}
                      </p>

                      {/* Features */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {theme.features.slice(0, 3).map((feature, index) => (
                          <span
                            key={index}
                            className="bg-gray-100 text-gray-700 px-2 py-1 rounded-lg text-xs font-medium"
                          >
                            {feature}
                          </span>
                        ))}
                        {theme.features.length > 3 && (
                          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-lg text-xs font-medium">
                            +{theme.features.length - 3} more
                          </span>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <span>{theme.rating}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Download className="w-4 h-4" />
                          <span>{theme.sales} sales</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(theme.lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        </div>
                      </div>

                      {/* Action Button */}
                      <motion.button
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold py-3 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center space-x-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Download className="w-4 h-4" />
                        <span>Get Template</span>
                      </motion.button>
                    </div>
                  </div>

                  {/* Background Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl opacity-0 group-hover:opacity-10 blur-xl -z-10 transition-all duration-500 transform group-hover:scale-105"></div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {/* Empty State */}
          {filteredThemes.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="text-gray-400 text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-600 mb-2">No themes found</h3>
              <p className="text-gray-500">
                Try adjusting your search or filter criteria to find what you're looking for.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
};

export default ThemesPage;